import React, { useState, useEffect } from 'react';
import { useApi } from '../hooks/useApi';

const IntegrationTest = () => {
  const [testResults, setTestResults] = useState(null);
  const [running, setRunning] = useState(false);
  const { apiService, apiConnected } = useApi();

  const runTests = async () => {
    setRunning(true);
    setTestResults(null);
    
    try {
      // Basic integration tests
      const results = {
        environment: testEnvironment(),
        api: await testApiService(),
        timestamp: new Date().toISOString()
      };
      setTestResults(results);
    } catch (error) {
      console.error('Test failed:', error);
      setTestResults({ error: error.message });
    } finally {
      setRunning(false);
    }
  };

  const testEnvironment = () => {
    return {
      nodeEnv: process.env.NODE_ENV,
      backendUrl: process.env.REACT_APP_BACKEND_URL,
      hasApiKey: !!process.env.REACT_APP_API_KEY,
      timestamp: new Date().toISOString()
    };
  };

  const testApiService = async () => {
    try {
      const response = await apiService.getCurrentUser();
      return {
        success: true,
        response: response,
        timestamp: new Date().toISOString()
      };
    } catch (error) {
      return {
        success: false,
        error: error.message,
        timestamp: new Date().toISOString()
      };
    }
  };

  const runIndividualTest = async (testName) => {
    setRunning(true);
    
    try {
      let result;
      switch (testName) {
        case 'environment':
          result = testEnvironment();
          break;
        case 'api':
          result = await testApiService();
          break;
        default:
          throw new Error('Unknown test');
      }
      
      setTestResults(prev => ({
        ...prev,
        [testName]: result
      }));
    } catch (error) {
      console.error(`${testName} test failed:`, error);
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-100 via-pink-100 to-yellow-100 dark:from-gray-900 dark:via-gray-800 dark:to-blue-900 p-8">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white/90 dark:bg-gray-800/90 rounded-3xl shadow-2xl p-8">
          <h1 className="text-3xl font-bold text-center mb-8 text-blue-700 dark:text-blue-200">
            🔧 Frontend-Backend Integration Test
          </h1>
          
          <div className="mb-8">
            <div className="flex gap-4 mb-4">
              <button
                onClick={runTests}
                disabled={running}
                className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold disabled:opacity-50"
              >
                {running ? 'Running Tests...' : 'Run All Tests'}
              </button>
              
              <button
                onClick={() => runIndividualTest('environment')}
                disabled={running}
                className="bg-green-500 hover:bg-green-600 text-white px-4 py-3 rounded-lg font-semibold disabled:opacity-50"
              >
                Test Environment
              </button>
              
              <button
                onClick={() => runIndividualTest('api')}
                disabled={running}
                className="bg-orange-500 hover:bg-orange-600 text-white px-4 py-3 rounded-lg font-semibold disabled:opacity-50"
              >
                Test API
              </button>
            </div>
            
            <div className="text-sm text-gray-600 dark:text-gray-400">
              <p>• <strong>Environment Test:</strong> Checks if all required environment variables are set</p>
              <p>• <strong>Supabase Test:</strong> Tests Supabase connection and authentication</p>
              <p>• <strong>API Test:</strong> Tests backend API connectivity</p>
              <p>• <strong>Endpoints Test:</strong> Tests individual API endpoints (requires backend running)</p>
            </div>
          </div>

          {/* API Connection Status */}
          <div className="mb-6 p-4 rounded-lg border">
            <h3 className="font-semibold mb-2">API Connection Status</h3>
            <div className="flex items-center gap-2">
              <span className={`w-3 h-3 rounded-full ${apiConnected ? 'bg-green-500' : 'bg-red-500'}`}></span>
              <span>{apiConnected ? '✅ Backend API Connected' : '❌ Backend API Not Connected'}</span>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              {apiConnected 
                ? 'Frontend can communicate with the backend API'
                : 'Backend is not running or not accessible. This is normal for frontend-only testing.'
              }
            </p>
          </div>

          {/* Test Results */}
          {testResults && (
            <div className="space-y-4">
              <h3 className="text-xl font-semibold">Test Results</h3>
              
              {testResults.error ? (
                <div className="p-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 rounded-lg">
                  <p className="text-red-700 dark:text-red-300">❌ Test Error: {testResults.error}</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Environment Test */}
                  {testResults.environment !== undefined && (
                    <div className={`p-4 rounded-lg border ${
                      testResults.environment 
                        ? 'bg-green-50 dark:bg-green-900/30 border-green-200 dark:border-green-700' 
                        : 'bg-red-50 dark:bg-red-900/30 border-red-200 dark:border-red-700'
                    }`}>
                      <h4 className="font-semibold mb-2">Environment Variables</h4>
                      <p className={testResults.environment ? 'text-green-700 dark:text-green-300' : 'text-red-700 dark:text-red-300'}>
                        {testResults.environment ? '✅ All required variables set' : '❌ Missing environment variables'}
                      </p>
                    </div>
                  )}

                  {/* Supabase Test */}
                  {testResults.supabase !== undefined && (
                    <div className={`p-4 rounded-lg border ${
                      testResults.supabase 
                        ? 'bg-green-50 dark:bg-green-900/30 border-green-200 dark:border-green-700' 
                        : 'bg-red-50 dark:bg-red-900/30 border-red-200 dark:border-red-700'
                    }`}>
                      <h4 className="font-semibold mb-2">Supabase Integration</h4>
                      <p className={testResults.supabase ? 'text-green-700 dark:text-green-300' : 'text-red-700 dark:text-red-300'}>
                        {testResults.supabase ? '✅ Supabase connected' : '❌ Supabase connection failed'}
                      </p>
                    </div>
                  )}

                  {/* API Connection Test */}
                  {testResults.apiConnection !== undefined && (
                    <div className={`p-4 rounded-lg border ${
                      testResults.apiConnection 
                        ? 'bg-green-50 dark:bg-green-900/30 border-green-200 dark:border-green-700' 
                        : 'bg-red-50 dark:bg-red-900/30 border-red-200 dark:border-red-700'
                    }`}>
                      <h4 className="font-semibold mb-2">API Connection</h4>
                      <p className={testResults.apiConnection ? 'text-green-700 dark:text-green-300' : 'text-red-700 dark:text-red-300'}>
                        {testResults.apiConnection ? '✅ Backend API reachable' : '❌ Backend API not reachable'}
                      </p>
                    </div>
                  )}

                  {/* Endpoints Test */}
                  {testResults.endpoints && (
                    <div className="p-4 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-700 rounded-lg md:col-span-2">
                      <h4 className="font-semibold mb-2">API Endpoints</h4>
                      <div className="space-y-2">
                        {testResults.endpoints.map((endpoint, index) => (
                          <div key={index} className="flex items-center gap-2">
                            <span className={endpoint.success ? 'text-green-600' : 'text-red-600'}>
                              {endpoint.success ? '✅' : '❌'}
                            </span>
                            <span className="font-mono text-sm">{endpoint.name}</span>
                            {!endpoint.success && (
                              <span className="text-xs text-gray-500">({endpoint.error})</span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Summary */}
              {testResults && !testResults.error && (
                <div className="mt-6 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <h4 className="font-semibold mb-2">Summary</h4>
                  {testResults.environment && testResults.supabase && testResults.apiConnection ? (
                    <p className="text-green-700 dark:text-green-300">
                      ✅ Frontend is properly configured and ready for production!
                    </p>
                  ) : testResults.environment && testResults.supabase ? (
                    <p className="text-yellow-700 dark:text-yellow-300">
                      ⚠️ Frontend is configured but backend is not running. This is normal for frontend-only testing.
                    </p>
                  ) : (
                    <p className="text-red-700 dark:text-red-300">
                      ❌ Some configuration issues detected. Check the individual test results above.
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Instructions */}
          <div className="mt-8 p-4 bg-blue-50 dark:bg-blue-900/30 rounded-lg">
            <h3 className="font-semibold mb-2">Testing Instructions</h3>
            <div className="text-sm space-y-1">
              <p>1. <strong>Frontend Only:</strong> Run "Test Environment" and "Test Supabase" to verify frontend configuration</p>
              <p>2. <strong>With Backend:</strong> Start the backend server first, then run "Test API" and "Test Endpoints"</p>
              <p>3. <strong>Production:</strong> All tests should pass when deployed with proper environment variables</p>
              <p>4. <strong>Debug:</strong> Check browser console for detailed test logs</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IntegrationTest; 